import { useCallback, useEffect, useRef, useState, memo, useMemo } from 'react';
import { css } from '@emotion/react';
import { CDTType, InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { useInView } from 'react-intersection-observer';
import { useReactToPrint } from 'react-to-print';
import { useGetCDTCodesForLocation, useGetInsuranceEligibilityReportEncodedHtml } from '@frontend/api-insurance';
import { IntegrationsQueries } from '@frontend/api-integrations';
import { useRemoveAppLoader } from '@frontend/document';
import { useTranslation } from '@frontend/i18n';
import { EligibilityReportWritebackModal } from '@frontend/insurance-verification-reports-writeback';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { StaticVerticalStepperContent } from '@frontend/static-vertical-stepper';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { SpinningLoader, useDebouncedValue, useModalControl, Text } from '@frontend/design-system';
import { Deductibles } from '../components/cards/deductibles';
import { FavoriteCDTCodesTableData } from '../components/cards/favorite-table';
import { FrequencyAndLimitations } from '../components/cards/frequency-limitations';
import { History } from '../components/cards/history';
import { Maximums } from '../components/cards/maximums';
import { PatientPayerDetails } from '../components/cards/patient-payer-details';
import { Provisions } from '../components/cards/provisions';
import { FavoritesBanner } from '../components/common-components/favorites-banner';
import { FiltersAndSearchSection } from '../components/filters-and-search/filter-and-search';
import { InsuranceReportHeader } from '../components/header/insurance-report-header';
import { PrintPortal } from '../components/printable-report/print-portal';
import { AccordionProvider } from '../context/AccordionContext';
import { InsuranceReportContainerProvider } from '../context/InsuranceReportContainerProvider';
import { useGetPayersList } from '../hooks/use-get-payers-list';
import { usePayerCredBanner } from '../hooks/use-payer-cred-banner';
import { useInsuranceReportData } from '../hooks/useInsuranceReportData';
import { useInsuranceReportShallowStore } from '../stores/use-insurance-report-store';
import { Benefit, CategoryCoverage, NetworkName } from '../types/insurance-report-api.types';
import {
  getNetworkNames,
  transformInsuranceReportApiDataForMaxAndDed,
  transformInsuranceReportApiStaticData,
  transformFrequencyAndLimitations,
  TransformPatientAndPayerDetailsReturnType,
  groupServiceDatesByProcedureCode,
  transformCategoryCoverage,
  decodeBase64Html,
} from '../utils/api-helpers';
import { DEBOUNCE_TIME_IN_MILLISECS, FAVORITE_CDT_CODES_ACCORDION_ID, STATIC_ACCORDION_IDS } from '../utils/constants';
import {
  getSearchDataOffavoriteAndRequestedCDTCodes,
  getSearchDataOfFrequencyAndLimitations,
} from '../utils/search-helpers';
import { insuranceReportTrackingIds } from '../utils/tracking-ids';
import { constructPatientsFullName, getDefaultNetworkFilter, getDefaultSearchValue } from '../utils/utils';

type InsuranceReportContainerProps = {
  reportProps: {
    locationId: string;
    personInfo: {
      personId: string;
      personName: string;
    };
    sourceId: string;
    personPMSId: string;
    insuranceType: InsuranceType;
    closeInsuranceReportModal?: () => void;
    onError: () => void;
    onManagePayerConnection?: (payerId: string, payerWebsiteId: string) => void;
  };
};

export type FilterKey = Pick<Benefit, 'networkType' | 'networkName'>;

type FilterState = NetworkName | string;

export type NonFilterableCardsDataType = {
  patientAndPayerDetails: TransformPatientAndPayerDetailsReturnType;
  provisions: string[];
  history: StaticVerticalStepperContent[];
};

export const InsuranceReportContainer = memo(({ reportProps }: InsuranceReportContainerProps) => {
  const {
    locationId,
    personInfo,
    sourceId,
    personPMSId,
    insuranceType,
    closeInsuranceReportModal,
    onError,
    onManagePayerConnection,
  } = reportProps;
  const { personId } = personInfo;
  const { ref, inView } = useInView({
    triggerOnce: true,
  });
  const { insuranceReportModalState } = useInsuranceReportShallowStore('insuranceReportModalState');
  const { setIsPersonId } = useInsuranceReportShallowStore('setIsPersonId');
  const { navigate } = useSettingsNavigate();
  const saveEligibilityReportModalProps = useModalControl();
  const { removeAppLoader } = useRemoveAppLoader({ removeOnMount: false });

  const { t } = useTranslation();
  const printRef = useRef<HTMLIFrameElement>(null);

  const [searchValue, setSearchValue] = useState<string>(getDefaultSearchValue(insuranceReportModalState.searchValue));
  const debounceSearchValue = useDebouncedValue(searchValue, DEBOUNCE_TIME_IN_MILLISECS);

  const [activeFilter, setActiveFilter] = useState<FilterState>(
    getDefaultNetworkFilter(insuranceReportModalState.filterValue)
  );
  const { PMSName, isLoading: isSyncAppDataLoading } = IntegrationsQueries.useIsWritebackSupported({
    locationId,
    sourceId: sourceId || '',
  });
  const [isTransformingData, setIsTransformingData] = useState(true);
  const [baseData, setBaseData] = useState({
    categoryCoverages: {} as Record<string, CategoryCoverage[]>,
    frequencyAndLimitations: {} as Record<string, Record<string, Benefit[]>>,
    favoriteCDTCodeNetworkFiltersData: {} as Record<string, Benefit[]>,
  });

  const { data: payersList, isLoading: isPayersLoading } = useGetPayersList({
    locationId: locationId || '',
    isEnhancedInsuranceVerificationEnabled: true,
    enabled: !!locationId,
  });

  const reactPrintHandler = useReactToPrint({
    content: () => printRef.current,
    documentTitle: 'Insurance Report',
    removeAfterPrint: true,
  });

  const handlePrint = useCallback(() => {
    reactPrintHandler();
  }, [reactPrintHandler]);

  const handleModalClose = useCallback(() => {
    setSearchValue('');
    setActiveFilter(NetworkName.inNetwork);
    setIsPersonId('');
    closeInsuranceReportModal?.();
  }, []);

  const {
    insuranceReportApiResponse,
    isError: isErrorInsuranceReport,
    isLoading: isLoadingReportData,
  } = useInsuranceReportData(personId, locationId, insuranceType);
  const { showBanner, handleCloseBanner } = usePayerCredBanner(insuranceReportApiResponse?.isEligibleForWebsiteResp);

  // Derive the payer connection IDs so the header can deep-link to the correct payer
  const payerId = insuranceReportApiResponse?.dentalSummaryInfo?.payerId || '';
  const payerWebsiteId = useMemo(
    () =>
      payersList?.find((payer) => payer.id === payerId || payer.alternatePayerIds?.includes(payerId))?.websiteId || '',
    [payersList, payerId]
  );

  const handleManagePayerConnection = useCallback(() => {
    if (onManagePayerConnection) {
      // Caller owns the store — let it set payerID/websiteID before navigating
      onManagePayerConnection(payerId, payerWebsiteId);
    } else {
      // Fallback: navigate without deep-linking to a specific payer
      navigate({ to: '/insurance/payer-connection-management' });
    }
  }, [onManagePayerConnection, payerId, payerWebsiteId, navigate]);

  const { data: favoriteData, isLoading: isLoadingFavoriteData } = useGetCDTCodesForLocation({
    locationId,
    cdtType: CDTType.CDT_TYPE_FAVORITE,
    enableFetchingLocationFavoritesAndRequestsCDTCodes: true,
    isEnhancedInsuranceVerificationEnabled: true,
  });

  const { data: requestsData, isLoading: isLoadingRequestsData } = useGetCDTCodesForLocation({
    locationId,
    cdtType: CDTType.CDT_TYPE_REQUESTED,
    enableFetchingLocationFavoritesAndRequestsCDTCodes: true,
    isEnhancedInsuranceVerificationEnabled: true,
  });

  const {
    isLoading: isLoadingEncodedHtml,
    isError: isErrorEncodedHtml,
    data: encodedHtmlData,
  } = useGetInsuranceEligibilityReportEncodedHtml(locationId, personId, insuranceType);

  const isLoading =
    isLoadingReportData ||
    isLoadingFavoriteData ||
    isLoadingRequestsData ||
    isPayersLoading ||
    isTransformingData ||
    isLoadingEncodedHtml;
  const isError = isErrorInsuranceReport || isErrorEncodedHtml;

  const decodedHTML = useMemo(() => decodeBase64Html(encodedHtmlData?.encodedHtml || ''), [encodedHtmlData]);

  const nonFilterableCardsData = useMemo(() => {
    if (insuranceReportApiResponse && payersList) {
      return transformInsuranceReportApiStaticData(insuranceReportApiResponse, payersList, t);
    }
    return {
      patientAndPayerDetails: {} as TransformPatientAndPayerDetailsReturnType,
      provisions: [] as string[],
      history: [] as StaticVerticalStepperContent[],
    };
  }, [insuranceReportApiResponse, payersList, t]);

  const networkNames = useMemo(
    () => getNetworkNames(insuranceReportApiResponse?.dentalSummaryInfo?.plan?.networks || []),
    [insuranceReportApiResponse?.dentalSummaryInfo?.plan?.networks]
  );

  useEffect(() => {
    if (inView) {
      pendo.track(insuranceReportTrackingIds.inlineReportVisible);
    }
  }, [inView]);

  useEffect(() => {
    // If the report query finished (success or error) but returned no data, stop the spinner
    if (!isLoadingReportData && !insuranceReportApiResponse) {
      setIsTransformingData(false);
      return;
    }

    if (!insuranceReportApiResponse) return;

    const transformApiData = () => {
      const transformedCategoryCoverages = transformCategoryCoverage(
        insuranceReportApiResponse?.dentalSummaryInfo?.plan?.categoryCoverages || []
      );

      const procedureCodeServiceDates = groupServiceDatesByProcedureCode(
        insuranceReportApiResponse?.dentalSummaryInfo?.plan?.services || []
      );

      const { segregatedDataForAllTables, favoriteCDTCodeNetworkFiltersData } =
        transformFrequencyAndLimitations(
          insuranceReportApiResponse?.dentalSummaryInfo?.plan?.benefits || [],
          networkNames,
          procedureCodeServiceDates,
          favoriteData?.cdtCodes || [],
          requestsData?.cdtCodes || []
        ) || {};

      setBaseData({
        categoryCoverages: transformedCategoryCoverages,
        frequencyAndLimitations: segregatedDataForAllTables || {},
        favoriteCDTCodeNetworkFiltersData: favoriteCDTCodeNetworkFiltersData || {},
      });
      setIsTransformingData(false);
    };
    // As this is a heavy operation making it async to allow paint first
    setTimeout(transformApiData, 0);
  }, [insuranceReportApiResponse, isLoadingReportData, networkNames, favoriteData, requestsData]);

  const {
    categoryCoverages,
    frequencyAndLimitations: cachedFrequencyLimitationData,
    favoriteCDTCodeNetworkFiltersData: cachedFavoriteAndRequestedCDTCodes,
  } = baseData;

  useEffect(() => {
    if (networkNames?.length > 0 && Object.keys(cachedFrequencyLimitationData).length) {
      // Logic to auto-select filter if current is invalid or empty
      const isCurrentFilterValid = activeFilter && networkNames.includes(activeFilter);

      if (!isCurrentFilterValid) {
        const defaultFilter =
          Object.keys(cachedFrequencyLimitationData[networkNames[0]] ?? {}).length === 0
            ? networkNames?.[1]
            : networkNames?.[0];

        if (defaultFilter) setActiveFilter(defaultFilter);
      }
    }
    // IsPersonId side effect
    if (personId) {
      setIsPersonId(personId);
    }
  }, [
    networkNames,
    cachedFrequencyLimitationData,
    activeFilter,
    personId,
    insuranceReportModalState.filterValue,
    setIsPersonId,
  ]);

  const { frequencyAndLimitations, activeCategoryCoverages, favoriteAndRequestedCDTCodes } = useMemo(() => {
    const rawFreq = cachedFrequencyLimitationData?.[activeFilter];
    const rawCats = categoryCoverages?.[activeFilter] || [];
    const rawFavs = cachedFavoriteAndRequestedCDTCodes;

    if (debounceSearchValue === '') {
      return {
        frequencyAndLimitations: rawFreq,
        activeCategoryCoverages: rawCats,
        favoriteAndRequestedCDTCodes: rawFavs,
      };
    } else {
      const searchData = getSearchDataOfFrequencyAndLimitations(rawFreq, debounceSearchValue);
      const searchDataCategories = Object.keys(searchData || {});

      const searchDataAccordionCategories = rawCats.filter((coverage) =>
        searchDataCategories.includes(coverage.category)
      );

      const searchDataFavs = getSearchDataOffavoriteAndRequestedCDTCodes(rawFavs, debounceSearchValue);

      return {
        frequencyAndLimitations: searchData,
        activeCategoryCoverages: searchDataAccordionCategories,
        favoriteAndRequestedCDTCodes: searchDataFavs,
      };
    }
  }, [
    cachedFrequencyLimitationData,
    categoryCoverages,
    cachedFavoriteAndRequestedCDTCodes,
    activeFilter,
    debounceSearchValue,
  ]);

  const maxAndDed = useMemo(() => {
    if (insuranceReportApiResponse) {
      return transformInsuranceReportApiDataForMaxAndDed(insuranceReportApiResponse, activeFilter);
    }
    return { maximums: [], deductibles: [] };
  }, [insuranceReportApiResponse, activeFilter]);

  const hasBenefitsWithNoCategoryCoverages =
    !categoryCoverages?.length && Object.keys(frequencyAndLimitations || {}).length;

  const accordionIds = useMemo(() => {
    return (activeCategoryCoverages || []).map((coverage) => coverage.category);
  }, [activeCategoryCoverages]);
  let activeCategoryAccordionId =
    debounceSearchValue === '' && accordionIds.length > 0 ? [accordionIds?.[0]] : accordionIds;
  activeCategoryAccordionId = hasBenefitsWithNoCategoryCoverages
    ? [Object.keys(frequencyAndLimitations || {})[0]]
    : activeCategoryAccordionId;

  const handleFilterChange = useCallback((value: string) => {
    setActiveFilter(value);
  }, []);

  const handleSaveToPMS = () => {
    // Removing the loader during printing the report
    removeAppLoader();
    saveEligibilityReportModalProps.openModal();
  };

  const onManageFavoritesClick = () => {
    insuranceReportModalState.setFilterValue(activeFilter);
    insuranceReportModalState.setSearchValue(searchValue);
    insuranceReportModalState.setInsuranceType(insuranceType);
    navigate({ to: '/insurance/cdt-code-management' });
  };

  useEffect(() => {
    if (isError) {
      onError();
    }
  }, [isError, onError]);

  if (isLoading || isSyncAppDataLoading) {
    return (
      <>
        <InsuranceReportHeader
          isReportLoading={isLoading}
          patientFullName={personInfo.personName}
          reportSources={insuranceReportApiResponse?.dentalSummaryInfo?.requests || []}
          verifiedDate={insuranceReportApiResponse?.lastVerifiedAt}
          handlePrint={handlePrint}
          handleSaveToPms={handleSaveToPMS}
          insuranceStatus={insuranceReportApiResponse?.dentalSummaryInfo?.plan?.status || ''}
          insuranceType={insuranceType}
          payerCredentialsBanner={{
            showBanner: showBanner,
            handleCloseBanner: handleCloseBanner,
            onManagePayerConnection: handleManagePayerConnection,
          }}
          closeInsuranceReportModal={handleModalClose}
        />
        <div css={loaderContainerStyles}>
          <SpinningLoader />
          <Text>{t('Please wait while we retrieve the eligibility report.')}</Text>
        </div>
      </>
    );
  }

  const patientsFullName = constructPatientsFullName(
    insuranceReportApiResponse?.dentalSummaryInfo?.patient?.firstName || '',
    insuranceReportApiResponse?.dentalSummaryInfo?.patient?.lastName || ''
  );
  return (
    <InsuranceReportContainerProvider locationId={locationId} sourceId={sourceId}>
      <div css={reportLayoutStyles}>
        <InsuranceReportHeader
          patientFullName={patientsFullName}
          reportSources={insuranceReportApiResponse?.dentalSummaryInfo?.requests || []}
          verifiedDate={insuranceReportApiResponse?.lastVerifiedAt}
          handlePrint={handlePrint}
          handleSaveToPms={handleSaveToPMS}
          insuranceStatus={insuranceReportApiResponse?.dentalSummaryInfo?.plan?.status || ''}
          insuranceType={insuranceType}
          payerCredentialsBanner={{
            showBanner: showBanner,
            handleCloseBanner: handleCloseBanner,
            onManagePayerConnection: handleManagePayerConnection,
          }}
          closeInsuranceReportModal={handleModalClose}
          isReportLoading={isLoading}
        />
        <div
          css={[
            reportStyles,
            {
              paddingTop: showBanner ? theme.spacing(18) : theme.spacing(9),
            },
          ]}
        >
          <FiltersAndSearchSection
            networkNames={networkNames}
            filterValue={activeFilter}
            handleFilterChange={handleFilterChange}
            onSearchChange={setSearchValue}
          />
          <div css={accordionStyles} ref={ref}>
            <AccordionProvider accordionIds={[...STATIC_ACCORDION_IDS, ...activeCategoryAccordionId]}>
              <div css={leftSideAccodordianDetailsStyles}>
                <PatientPayerDetails data={nonFilterableCardsData?.patientAndPayerDetails?.report || []} />
                <Maximums maximums={maxAndDed.maximums} />
                <Deductibles deductibles={maxAndDed.deductibles} />
                <Provisions provisions={nonFilterableCardsData?.provisions || []} />
                <History history={nonFilterableCardsData?.history || []} />
              </div>
              <div css={rightSideAccodordianDetailsStyles}>
                {favoriteData?.cdtCodes?.length || requestsData?.cdtCodes?.length ? (
                  <FavoriteCDTCodesTableData
                    accordionTitle={t('Favorite Procedures')}
                    accordionId={FAVORITE_CDT_CODES_ACCORDION_ID}
                    data={favoriteAndRequestedCDTCodes?.[activeFilter] || []}
                    onManageFavorites={onManageFavoritesClick}
                  />
                ) : (
                  <FavoritesBanner onManageFavorites={onManageFavoritesClick} />
                )}
                <FrequencyAndLimitations
                  benefits={frequencyAndLimitations}
                  categoryCoverages={activeCategoryCoverages}
                  hasFavoriteCDTCodes={!!favoriteData?.cdtCodes?.length}
                />
              </div>
            </AccordionProvider>
          </div>
        </div>
      </div>
      <PrintPortal>
        <div ref={printRef}>
          <div dangerouslySetInnerHTML={{ __html: decodedHTML || '' }} />
        </div>
      </PrintPortal>
      <EligibilityReportWritebackModal
        PMSName={PMSName}
        locationId={locationId}
        personPMSId={personPMSId}
        sourceId={sourceId}
        PrintableReport={<div dangerouslySetInnerHTML={{ __html: decodedHTML || '' }} />}
        {...saveEligibilityReportModalProps}
      />
    </InsuranceReportContainerProvider>
  );
});

InsuranceReportContainer.displayName = 'InsuranceReportContainer';

const reportLayoutStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
  overflow: auto;
`;

const reportStyles = css`
  display: flex;
  flex-direction: column;
  margin: ${theme.spacing(3)};
`;

const leftSideAccodordianDetailsStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
  width: 30%;
`;
const accordionStyles = css`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing(3)};
  width: 100%;
  margin-top: ${theme.spacing(2)};
`;
const rightSideAccodordianDetailsStyles = css`
  display: flex;
  flex-direction: column;
  width: 70%;
`;

export const provisionAndHistoryStyles = css`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing(2)};
`;

const loaderContainerStyles = css`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
